<template>
  <div class="check-order">
    <div class="food-items-container">
      <div class="food-items-container__header">
        <div class="food-items-container__title">
          <h2>{{ $t('OrderFood.FoodItemList') }}</h2>
          <el-button
            size="mini"
            round
            @click="$emit('gotoTab', 1)"
          >
            {{ $t('General.Edit')+$t('OrderFood.FoodItem') }}
          </el-button>
        </div>
        <div v-show="currentSelectedHouseholdInfo.tabletId" class="food-items-container__household-info">
          <span>{{ $t('OrderFood.Household') }}：{{ currentSelectedHouseholdInfo.tabletNote }}</span>
          <span>{{ $t('OrderFood.NormalPoints') }}：{{ currentSelectedHouseholdInfo.normalPoint }}</span>
          <span>{{ $t('OrderFood.CashPoints') }}：{{ currentSelectedHouseholdInfo.cashPoint }}</span>
        </div>
      </div>
      <el-table
        :data="mergedSelectedFoodItems"
        :empty-text="$t('OrderFood.NoItems')"
        border
        style="width: 80%"
      >
        <el-table-column
          :label="$t('OrderFood.FoodCategory')"
          prop="menuType"
        />
        <el-table-column
          :label="$t('OrderFood.FoodItem')"
          prop="name"
        />
        <el-table-column
          :label="$t('OrderFood.Quantity')"
          prop="quantity"
          width="100"
        />
        <el-table-column
          :label="$t('OrderFood.Point')"
          width="100"
        >
          <template slot-scope="scope">
            {{ scope.row.prices[selectedPointType - 1].unitPrice }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('OrderFood.SpecOptions')"
        >
          <template slot-scope="scope">
            {{ scope.row.displaySpec || $t('OrderFood.NoSpec') }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('OrderFood.Subtotal')"
          width="150"
        >
          <template slot-scope="scope">
            {{ scope.row.quantity * scope.row.prices[selectedPointType - 1].unitPrice }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('OrderFood.Total')"
          width="150"
        >
          <template>
            {{ estimatedCost }}
          </template>
        </el-table-column>
      </el-table>
    </div>
    <div class="point-container">
      <h2 class="mb-10">
        {{ $t('OrderFood.PointsTransaction') }}
      </h2>
      <el-table
        :data="pointArr"
        :empty-text="$t('OrderFood.NoPointsTransactionInfo')"
        border
        style="width: 100%"
      >
        <el-table-column
          align="center"
          :label="$t('OrderFood.TransactionType')"
          prop="dealType"
        />
        <el-table-column
          align="center"
          :label="$t('OrderFood.PointsType')"
          prop="pointTypeZh"
        />
        <el-table-column
          align="center"
          :label="$t('OrderFood.PointsCalculation')"
        >
          <template slot-scope="scope">
            <div class="hint">
              {{ $t('OrderFood.TotalPoints') }} {{ scope.row.estimatedCost }}
            </div>
            <div class="actualCost">
              <el-input
                v-model="scope.row.actualCost"
                style="text-align:center"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column
          align="center"
          :label="$t('OrderFood.Note')"
        >
          <template slot-scope="scope">
            <el-input
              v-model="scope.row.remark"
              placeholder="不可超過 100 字元"
              maxlength="100"
            />
          </template>
        </el-table-column>
      </el-table>
    </div>
    <div class="printers-container">
      <h2 class="mb-10">
        {{ $t('OrderFood.ReceiptPrinter') }}
      </h2>
      <div v-if="printersWithStatusAndResult.length===0" class="printers-no-ip">
        {{ $t('OrderFood.ReceiptPrinterNoIp') }}<span>{{ $t('OrderFood.SmartDailyPhoneTip') }}</span>
      </div>
      <div v-else>
        <div
          v-for="item in printersWithStatusAndResult"
          :key="item.Ip"
          class="printer-status"
        >
          <span class="title">{{ item.Remark }}：</span>
          <span class="status">{{ item.Status }}</span>
          <span
            v-if="item.Status === $t('OrderFood.ConnectionFailed')"
            class="status"
          >
            <a href="https://knst.tw/97ms2e" target="_blank">
              {{ $t('OrderFood.IsReceiptPrinterConnectFailed') }}
            </a>
          </span>
        </div>
      </div>
    </div>
    <!-- 訊息對話框 -->
    <message-dialog
      v-bind="messageDialogOptions"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <point-supplement-template
        v-if="isPointSupplementEnabled && isPointSupplementEnough"
        :point-type="selectedPointType"
        :original-points="pointSupplementDialogOptions.originalPoints"
        :supplement-points="pointSupplementDialogOptions.supplementPoints"
        :supplement-original-points="pointSupplementDialogOptions.supplementOriginalPoints"
      />
    </message-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { apiPoint } from '@/api/point/point.js'
import { apiOrderFood } from '@/api/OrderFood'
import { getCommunityTabletPointsData, getDiningDisplayText, formatOrderDisplayCode } from '@/js/utils/orderFood'
import { testPrinterConnection, printThermalPrinter, LATEST_RECEIPT_TEMPLATE_VERSION } from '@/js/utils/epos.js'
import { convertKeysToPascalCase, convertKeysToCamelCase } from '@/js/utils/bff'
import MessageDialog from './MessageDialog.vue'
import PointSupplementTemplate from './PointSupplementTemplate.vue'
export default {
  name: 'PointConfirmation',
  components: {
    MessageDialog,
    PointSupplementTemplate
  },
  props: {
    // 點數種類
    selectedPointType: {
      type: Number,
      default: 1
    },
    // 用餐方式
    selectedDiningMode: {
      type: Number,
      default: 1
    },
    // 內用桌號
    selectedTableNo: {
      type: String,
      default: ''
    },
    // 項目清單
    selectedFoodItems: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      mergedSelectedFoodItems: [], // 合併相同項目的項目清單
      pointArr: [], // 點數交易資訊
      estimatedCost: 0, // 預估使用點數
      printersWithStatusAndResult: [],
      // 訊息對話框設定值
      messageDialogOptions: {},
      // 點數補足時的對話框參數
      pointSupplementDialogOptions: {
        originalPoints: 0, // 原始點數
        supplementPoints: 0, // 補足點數
        supplementOriginalPoints: 0 // 補足點數的原始點數
      },
      loading: null // 載入中
    }
  },
  computed: {
    ...mapState({
      currentSelectedHouseholdInfo: state => state.orderFood.currentTransaction.selectedHouseholdInfo.current,
      isPointSupplementEnabled: state => state.orderFood.currentTransaction.isPointSupplementEnabled,
      isPointSupplementEnough: state => state.orderFood.currentTransaction.isPointSupplementEnough
    }),
    isNormalToCashEnabled () {
      return this.$store.getters.isServiceEnabled(61) // 「一般點數」補足「現金點數」開關
    },
    isCashToNormalEnabled () {
      return this.$store.getters.isServiceEnabled(62) // 「現金點數」補足「一般點數」開關
    }
  },
  watch: {
    selectedFoodItems: {
      handler: function () {
        this.handleMergedSelectedFoodItems() // 合併相同規格的餐點品項
        this.setPointArr() // 若項目清單有異動則要更新點數交易資訊
      },
      deep: true,
      immediate: true
    },
    pointArr: {
      handler: function (newVal, oldVal) {
        // 實際使用點數只能為非負整數
        newVal.forEach(el => {
          if (el.actualCost) {
            el.actualCost = el.actualCost.toString().replace(/[^0-9]/, '')
          }
        })
      },
      deep: true,
      immediate: true
    }
  },
  async mounted () {
    await this.initPrinters()
  },
  methods: {
    /**
     * 處理合併相同規格的餐點品項
     */
    handleMergedSelectedFoodItems () {
      const clonedSelectedFoodItems = _cloneDeep(this.selectedFoodItems)
      // 合併相同項目的項目清單
      const tempList = []
      clonedSelectedFoodItems.forEach(item => {
        const index = tempList.findIndex(ms => ms.id === item.id && ms.displaySpec === item.displaySpec)
        if (index !== -1) {
          tempList[index].quantity += item.quantity
        } else {
          tempList.push(item)
        }
      })
      this.mergedSelectedFoodItems = [...tempList.sort((a, b) => a.id - b.id)]
    },
    /**
     * 設定點數交易資訊
     */
    setPointArr () {
      // 交易類型
      const dealType = '使用'
      // 點數種類
      const pointTypeZh = this.$store.getters.getPointTypeTitle(this.selectedPointType)
      // 預估使用點數
      const estimatedCost = this.mergedSelectedFoodItems.reduce(
        (accumulator, currentValue) => accumulator + currentValue.quantity * currentValue.prices[this.selectedPointType - 1].unitPrice,
        0)
      this.estimatedCost = estimatedCost
      // 實際使用點數
      const actualCost = estimatedCost
      // 預估剩餘點數 = 目前點數 - 實際使用點數
      this.pointArr = [{
        dealType,
        pointTypeZh,
        estimatedCost,
        actualCost
      }]
    },
    /**
     * 初始化出單機
     */
    async initPrinters () {
      // 獲取出單機資訊、初始化連線
      try {
        const res = await apiPoint.getCommunityPrinters()
        const printers = res.data.Data.Printers
        // 本機開發測試出單機
        const testPrinter = {
          Ip: '192.168.6.139',
          Remark: 'RD 測試'
        }
        // 如果在本機端開發、且有給 testPrinter，則需新增該台測試用出單機
        const allPrinters = (process.env.NODE_ENV === 'development') && testPrinter ? [testPrinter, ...printers] : [...printers]
        // 初始化連線狀態
        this.printersWithStatusAndResult = allPrinters.map(el => {
          el.Status = '連線中'
          return el
        })
        // 非同步地分別連線各台出單機
        this.connectAllPrinters(this.printersWithStatusAndResult)
      } catch (error) {
        console.log(error)
      }
    },
    /**
     * 連線所有出單機
     * @param printers 出單機清單
     */
    async connectAllPrinters (printers) {
      // 不同步地連線所有出單機，但要分別回傳連線結果
      const connectPrinter = async (printerObj) => {
        // 為了要回傳連線結果故使之同步
        const res = await testPrinterConnection(printerObj.Ip)
        printerObj.Status = res === 'success' ? this.$t('OrderFood.ConnectionSuccess') : this.$t('OrderFood.ConnectionFailed')
      }
      // forEach 寫出不同步的部分
      printers.forEach(printer => {
        connectPrinter(printer)
      })
    },
    /**
     * 處理送出
     * @param {number} type 扣點方式(1: 直接選擇戶別, 2: 手機條碼, 3: 門禁卡、磁扣)
     * @param {object} selectedHousehold 選擇戶別資料
     */
    async submit (type, selectedHousehold) {
      this.loading = this.showLoading()
      /**
       * 先重置相關狀態
       * 1. 是否啟用點數補足功能
       * 2. 是否以第二個點數類型足以補足
       */
      this.$store.commit('setCurrentTransactionIsPointSupplementEnabled', false)
      this.$store.commit('setCurrentTransactionIsPointSupplementEnough', false)

      let toastErrorMessage = '' // 錯誤訊息
      let isPointsInsufficient = false // 是否點數不足
      try {
        let tabletId = '' // 戶別ID
        let tabletNote = '' // 戶別名稱
        let paymentId = '' // 扣點ID
        // 因應不同扣點方式取得的戶別資料內容不同
        switch (type) {
        case 1: // 直接選擇戶別
          tabletId = selectedHousehold.TabletId
          tabletNote = selectedHousehold.TabletNote
          break
        case 2: // 手機條碼
          tabletId = selectedHousehold.tabletId
          tabletNote = selectedHousehold.tabletNote
          paymentId = selectedHousehold.userId
          break
        case 3: // 門禁卡、磁扣
          tabletId = selectedHousehold.tabletId
          tabletNote = selectedHousehold.tabletNote
          paymentId = selectedHousehold.carrierId
          break
        }

        // 查詢用戶點數
        const { status: getPointStatus, data } = await getCommunityTabletPointsData(tabletId)
        if (getPointStatus === 1) {
          const normalPoint = data.pointTablets.find(item => item.ptId === 0)?.point // 一般點數
          const cashPoint = data.pointTablets.find(item => item.ptId !== 0)?.point // 現金點數
          const currentPointValue = this.selectedPointType === 1 ? normalPoint : cashPoint // 目前選擇點數類型的點數
          if (currentPointValue === undefined || currentPointValue === null) {
            toastErrorMessage = this.$t('OrderFood.GetTabletPointsFailed')
            throw new Error(toastErrorMessage)
          }

          // 如果尚未紀錄任何戶別資訊
          if (!this.currentSelectedHouseholdInfo.tabletId) {
            // 紀錄戶別資訊
            this.$store.commit('setCurrentTransactionHouseholdInfo', { tabletId, tabletNote, normalPoint, cashPoint })
          } else {
            // 如果所選戶別不一致
            if (tabletId !== this.currentSelectedHouseholdInfo.tabletId) {
              this.loading.close()
              // 確認是否以目前感應的扣點戶別來扣點
              const isConfirm = await this.showConfirmDialog({
                title: this.$t('OrderFood.SelectedHouseholdDifferent'),
                message: this.$t('OrderFood.SelectedDifferentHouseholdConfirm', {
                  tabletNotePrevious: this.currentSelectedHouseholdInfo.tabletNote,
                  tabletNoteCurrent: tabletNote
                }),
                showCancelButton: true,
                confirmButtonText: this.$t('OrderFood.ConfirmDeduct'),
                onConfirm: () => {
                  // 點選確認後，覆蓋先前的戶別資訊
                  this.$store.commit('setCurrentTransactionHouseholdInfo', { tabletId, tabletNote, normalPoint, cashPoint })
                }
              })
              if (!isConfirm) {
                this.$emit('gotoTab', 3) // 回到點數交易步驟
                return
              }
            }
          }

          // 更新點數交易資訊
          Object.assign(this.pointArr[0], {
            tabletNote,
            normalPoint,
            cashPoint,
            currentPointValue
          })
          const actualCost = Number(this.pointArr[0].actualCost)

          // 住戶持有點數無法直接支付訂單
          isPointsInsufficient = currentPointValue - actualCost < 0
          if (isPointsInsufficient) {
            /**
             * 檢查是否啟用補足功能
             * 1. 若為一般點數，判斷是否啟用「現金補一般」功能
             * 2. 若為現金點數，判斷是否啟用「一般補現金」功能
             */
            const isSupplementEnabled = (this.selectedPointType === 1 && this.isCashToNormalEnabled) ||
              (this.selectedPointType === 2 && this.isNormalToCashEnabled)

            if (isSupplementEnabled) {
              /**
               * 設定此次交易使用點數補足功能
               * 1. 因應開關狀態，設定為使用補足功能。
               */
              this.$store.commit('setCurrentTransactionIsPointSupplementEnabled', true)

              // 判斷點數總和是否足夠支付
              const totalPoints = normalPoint + cashPoint
              if (totalPoints >= actualCost) {
                // 設定第二個點數類型足以補足
                this.$store.commit('setCurrentTransactionIsPointSupplementEnough', true)

                // 依照交易類型，計算需補足點數
                const supplementPoints = this.selectedPointType === 1
                  ? actualCost - normalPoint // 一般點數不足，用現金點數補足
                  : actualCost - cashPoint // 現金點數不足，用一般點數補足

                // 設定點數補足對話框參數
                this.pointSupplementDialogOptions = {
                  originalPoints: currentPointValue, // 交易類型點數的原始點數
                  supplementPoints, // 需補足點數
                  supplementOriginalPoints: this.selectedPointType === 1 ? cashPoint : normalPoint // 補足點數類型的原始點數
                }

                this.loading.close()
                // 顯示「點數補足確認」對話框
                const isConfirmSupplement = await this.showConfirmDialog({
                  title: this.$t('OrderFood.PointsSupplementDialogTitle'),
                  showCancelButton: true,
                  confirmButtonText: this.$t('OrderFood.ConfirmSupplement'),
                  cancelButtonText: this.$t('OrderFood.EditFoodItem'),
                  width: '50%'
                })
                if (isConfirmSupplement) {
                  // 使用者確認補足，繼續交易
                  isPointsInsufficient = false
                } else {
                  // 點擊編輯餐點品項，回到編輯頁面
                  this.$emit('gotoTab', 1)
                  return
                }
              } else {
                // 第二個點數類型是否足以補足 => N (#2)
              }
            } else {
              // 住戶所屬社區是否開啟EIP點數補足開關 => N (#2)
            }
            // 紀錄目前交易的點數不足狀態
            this.$store.commit('setCurrentTransactionPointsInsufficient', isPointsInsufficient)
          }
        } else {
          // 取得戶別點數失敗，直接跳出
          toastErrorMessage = this.$t('OrderFood.GetTabletPointsFailed')
          throw new Error(toastErrorMessage)
        }

        // #2 如果點數不足且未在補足邏輯中處理，則顯示一般點數不足訊息
        if (isPointsInsufficient) {
          this.loading.close()
          await this.showConfirmDialog({
            title: this.$t('OrderFood.PointsInsufficient'),
            message: this.$t('OrderFood.PointsInsufficientAlert', { tabletNote }),
            confirmButtonText: this.$t('OrderFood.EditFoodItem')
          })
          this.$emit('gotoTab', 1) // 回到選擇餐點品項頁籤
          return
        }

        this.loading = this.showLoading(this.$t('General.Processing'))
        // 整理新增訂單參數
        const params = {
          tabletId,
          priceType: this.selectedPointType, // 點數類別
          paymentType: type, // 扣點方式
          paymentId, // 扣點ID
          // 補單使用
          thermalPaper: JSON.stringify(this.mergedSelectedFoodItems.map(item => {
            return {
              name: item.name,
              quantity: item.quantity,
              price: item.prices[this.selectedPointType - 1].unitPrice,
              displaySpec: item.displaySpec
            }
          })),
          actualAmount: Number(this.pointArr[0].actualCost),
          note: this.pointArr[0].remark,
          items: [] // 餐點品項
        }
        // 處理餐點品項
        this.mergedSelectedFoodItems.forEach(el => {
          const newItem = {
            menuSettingId: el.id,
            menuPriceId: el.prices[this.selectedPointType - 1].id,
            groupName: el.menuType,
            itemName: el.name,
            chooseName: el.displaySpec,
            unitPrice: el.prices[this.selectedPointType - 1].unitPrice,
            quantity: el.quantity,
            note: '',
            chooses: el.chooses.flatMap((spec) => spec.children)
              .filter((opt) => opt.isChecked)
              .map((opt) => { return { specSettingId: opt.id, name: opt.name } })
          }
          params.items.push(newItem)
        })

        // 新增點餐訂單（TableFood/Form）
        const apiMethod = this.isPointSupplementEnabled ? apiOrderFood.addFoodFormModeV3 : apiOrderFood.addFoodFormSingleV3
        // 用餐方式
        params.dining = {
          diningOption: this.selectedDiningMode,
          tableNo: this.selectedTableNo
        }

        const apiResp = await apiMethod(convertKeysToPascalCase(params))
        const respData = convertKeysToCamelCase(apiResp.data)

        const { status, errorMessage, data: orderData } = respData
        if (status === 1) {
          // 出單開始：訂單出單張數為 0 時不出單
          const copies = await this.$store.dispatch('resolveFormPrintCount', { isAbnormal: false })
          const printResult = copies === 0 ? null : await this.print(orderData, copies)
          // 父層觸發點數交易狀態與出單狀態（null 表示出單設定關閉、未出單）
          this.$emit('printResult', printResult)
        } else {
          toastErrorMessage = errorMessage
          throw new Error(errorMessage)
        }
      } catch (error) {
        console.log(error)
        // 讓後端回傳的 ErrorMessage.Description 可以顯示在 UI
        const apiErrorMessage = error?.response?.data?.ErrorMessage
        if (typeof apiErrorMessage === 'string') {
          toastErrorMessage = apiErrorMessage
        } else if (apiErrorMessage?.Description) {
          toastErrorMessage = apiErrorMessage.Description
        }
        // 顯示錯誤訊息對話框
        this.showConfirmDialog({
          title: this.$t('OrderFood.PointsTransactionFailed'),
          message: toastErrorMessage || this.$t('OrderFood.PointsTransactionFailedTryAgain'),
          confirmButtonText: this.$t('General.Confirm'),
          width: '15%'
        })
      } finally {
        this.loading.close()
      }
    },
    /**
     * 顯示確認對話框並等待用戶操作
     * @param {Object} options 對話框選項
     * @returns {Promise<boolean>} 用戶選擇結果，確認為 true，取消為 false
     */
    showConfirmDialog (options = {}) {
      return new Promise((resolve, reject) => {
        this.messageDialogOptions = {
          visible: true,
          title: options.title,
          message: options.message,
          showCancelButton: options.showCancelButton || false,
          cancelButtonText: options.cancelButtonText || this.$t('General.Cancel'),
          confirmButtonText: options.confirmButtonText || this.$t('General.Confirm'),
          customClass: options.customClass || 'sm-size',
          width: options.width || '',
          // 此處因為需要處理 Promise，所以沒有直接將 onConfirm 和 onCancel 當作參數
          onConfirm: () => {
            // 如果用戶點擊確認按鈕，則執行傳入的 onConfirm 函數
            if (typeof options.onConfirm === 'function') {
              options.onConfirm()
            }
            this.messageDialogOptions.visible = false
            resolve(true)
          },
          onCancel: () => {
            if (typeof options.onCancel === 'function') {
              options.onCancel()
            }
            this.messageDialogOptions.visible = false
            resolve(false)
          }
        }
      })
    },
    /**
     * 列印訂單
     * @param {Object} orderData 訂單資料
     * @param {string} orderData.comOrderId 訂單編號
     * @param {string} orderData.createdAtUtc 訂單建立時間
     * @param {string} orderData.id 訂單識別碼
     * @param {number} copies 每台出單機列印張數
     * @returns {Promise<Object>} 列印結果
     */
    async print (orderData, copies = 1) {
      // 整理餐點品項列表
      const itemArr = this.mergedSelectedFoodItems.map(el => {
        return {
          name: el.name, // 餐點品項名稱
          quantity: el.quantity, // 數量
          price: el.prices[this.selectedPointType - 1].unitPrice, // 單價(點數)
          displaySpec: el.displaySpec // 規格
        }
      })

      const actualCost = Number(this.pointArr[0].actualCost) // 交易點數合計
      const normalPointBefore = this.pointArr[0].normalPoint // 一般點數(交易前)
      const cashPointBefore = this.pointArr[0].cashPoint // 現金點數(交易前)
      let normalPointAfter = this.pointArr[0].normalPoint // 一般點數(交易後)
      let cashPointAfter = this.pointArr[0].cashPoint // 現金點數(交易後)
      // 是否有啟用點數補足功能
      if (this.isPointSupplementEnabled) {
        if (this.selectedPointType === 1) { // 現金點數補足一般點數
          normalPointAfter = normalPointBefore + this.pointSupplementDialogOptions.supplementPoints - actualCost
          cashPointAfter = cashPointBefore - this.pointSupplementDialogOptions.supplementPoints
        } else { // 一般點數補足現金點數
          normalPointAfter = normalPointBefore - this.pointSupplementDialogOptions.supplementPoints
          cashPointAfter = cashPointBefore + this.pointSupplementDialogOptions.supplementPoints - actualCost
        }
      } else {
        if (this.selectedPointType === 1) { // 一般點數
          normalPointAfter = normalPointBefore - actualCost
        } else { // 現金點數
          cashPointAfter = cashPointBefore - actualCost
        }
      }

      // 取得用餐方式文字
      const computedDiningText = getDiningDisplayText({ diningOption: this.selectedDiningMode, tableNo: this.selectedTableNo })
      // 列印小白單資料
      const formattedData = {
        comOrderId: formatOrderDisplayCode(orderData.comOrderId, orderData.createdAtUtc), // 訂單編號
        dining: computedDiningText, // 用餐方式
        userId: this.pointArr[0].tabletNote, // 戶號
        items: itemArr, // 餐點品項列表
        isPointSupplement: this.isPointSupplementEnabled, // 是否啟用點數補足功能
        selectedPointType: this.selectedPointType, // 選擇的交易點數類別
        totalPoint: actualCost, // 交易點數合計
        normalPointBefore, // 一般點數(交易前)
        cashPointBefore, // 現金點數(交易前)
        normalPointAfter, // 一般點數(交易後)
        cashPointAfter, // 現金點數(交易後)
        staff: JSON.parse(localStorage.getItem('loginUserInfo')).AdminName || '', // 服務員
        note: this.pointArr[0].remark || '', // 備註
        date: dayjs().format('YYYY-MM-DD HH:mm:ss') // 時間
      }
      // 使各台出單機均印出單據
      try {
        const res = await this.printAllPrinters(this.printersWithStatusAndResult, formattedData, copies)
        // 回傳出單結果
        return res
      } catch (error) {
        console.log(error)
        // 回傳出單結果
        return this.printersWithStatusAndResult.map(el => el.Result = this.$t('OrderFood.PrintFailedAndGoRecordReprint'))
      }
    },
    async printAllPrinters (printers, data, copies = 1) {
      const promiseArr = printers.map(el => printThermalPrinter(LATEST_RECEIPT_TEMPLATE_VERSION, el.Ip, data, undefined, { copies }))
      try {
        // 每台出單機都需要回傳出單結果
        const res = await Promise.allSettled(promiseArr)
        printers.forEach((el, index) => {
          el.IsFulfilled = res[index].status === 'fulfilled'
          el.Result = res[index].status === 'fulfilled' ? this.$t('OrderFood.PrintSuccess') : this.$t('OrderFood.PrintFailedAndGoRecordReprint')
        })
        return printers
      } catch (error) {
        console.log(error)
      }
    },
    /**
     * 顯示載入中
     * @param {string} text 載入中文字
     * @returns {Object} 載入中實例
     */
    showLoading (text) {
      return this.$loading({
        lock: true,
        text: text || this.$t('General.PleaseWaitFor', { action: this.$t('General.Processing') }),
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  h2 {
    font-weight: bold;
    font-size: map-get($fontSize, 'xx-large');
    // margin-bottom: 10px;
    text-align: left;
  }
  .check-order {
    display: flex;
    flex-direction: column;
    gap: 20px;
    .food-items-container {
      display: flex;
      flex-direction: column;
      &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 15px;
      }
      &__title {
        display: flex;
        align-items: center;
        gap: 20px;
      }
      &__household-info {
        display: flex;
        align-items: center;
        font-size: map-get($fontSize, small);
        & span:not(:last-child) {
          margin-right: map-get($fontSize, x-large);
        }
      }
    }
    ::v-deep .el-table {
      .active {
        color: $dangerColor;
      }
      tr th {
        background-color: $bgDark3;
        color: $subFontColor;
      }
      tr:hover {
        > td {
          background-color: unset;
        }
      }
      .point-container {
        padding: 0;
        .cell {
          padding: 0;
        }
      }
      .point-wrapper {
        display: grid;
        grid-template-columns: 6fr 4fr;
        grid-gap: 1px;
        background-color: $borderDark1;
        div {
          background-color: #fff;
          padding: 10px;
        }
      }
      .actualCost {
        input {
          text-align: center;
        }
      }
    }
    .printers-container {
      display: flex;
      flex-direction: column;
      gap: 10px;
      .printers-no-ip {
        text-align: left;
        span {
          color: $mainColor
        }
      }
      .printer-status {
        text-align: start;
        a {
          color: $mainColor;
          &:hover {
            color: $mainColorDark;
          }
        }
      }
    }
  }
</style>
